export const appNam = 'AB tools';
export const DATE_FORMAT_MMDDYYYY = 'MM/DD/YYYY';
export const DATE_FORMAT_MMDDYYYYHHMMA = 'MM/DD/YYYY hh:mm A';
export const NO_MATCHING_RECORDS_FOUND = 'No matching records found.';

export const WorkQueueTabConstant = {
  INVOLVED_PARTIES: 'involved-parties',
  AGREEMENT: 'agreement',
  STATUS_HISTORY: 'status-history',
  COMMENTS: 'comments',
  Scoring: 'scoring',
};

export const NETWORK_ERROR = {
  error: 'Network Error',
  message: `Application seems to be not responding. Please try again after 15 minutes. 
    If the issue still persists, raise a Service Now Ticket`,
  code: 502,
};

export const GENERIC_ERROR = `Something went wrong. Please try again after 15 minutes. 
If the issue still persists, raise a Service Now Ticket`;

export const WORKLIST_ID_CHAR_VALIDATION_ERROR = 'WorkList ID should be alphanumeric and 7 characters';

export const PEGA_URL = 'https://jdf-pega.deere.com/prweb/sso1';

// AD Group names (uppercase, as received from Okta after .toUpperCase())
export const AD_GROUPS = {
  ABTOOLS_SYSTEM_ADMIN: 'ABTOOLS_SYSTEM_ADMIN',
  ABTOOLS_CREDIT_ANALYST: 'ABTOOLS_CREDIT_ANALYST',
  ABTOOLS_PROCESSING_STAFF: 'ABTOOLS_PROCESSING_STAFF',
  AD_ABTOOLS_PROD: 'AD_ABTOOLS_PROD',
  ABTOOLS_NON_PROD: 'ABTOOLS_NON_PROD',
  ABTOOLS_PROD_SUPPORT: 'ABTOOLS_PROD_SUPPORT',
};

/**
 * Access matrix by environment (APP_ENV injected at build time via DotenvPlugin):
 *
 *  devl  → full access : ABTOOLS_NON_PROD
 *          readonly    : ABTOOLS_PROD_SUPPORT
 *          no access   : ABTOOLS_SYSTEM_ADMIN, ABTOOLS_CREDIT_ANALYST,
 *                        ABTOOLS_PROCESSING_STAFF, AD_ABTOOLS_PROD
 *
 *  qual  → full access : ABTOOLS_SYSTEM_ADMIN, ABTOOLS_CREDIT_ANALYST,
 *                        ABTOOLS_PROCESSING_STAFF, ABTOOLS_NON_PROD
 *          readonly    : ABTOOLS_PROD_SUPPORT
 *          no access   : AD_ABTOOLS_PROD
 *
 *  prod  → full access : ABTOOLS_SYSTEM_ADMIN, ABTOOLS_CREDIT_ANALYST,
 *                        ABTOOLS_PROCESSING_STAFF, AD_ABTOOLS_PROD
 *          readonly    : ABTOOLS_PROD_SUPPORT
 *          no access   : ABTOOLS_NON_PROD
 *          (ABTOOLS_NON_PROD is NOT allowed on prod)
 */

const PROD_FULL_ACCESS_GROUPS = [
  AD_GROUPS.ABTOOLS_SYSTEM_ADMIN,
  AD_GROUPS.ABTOOLS_CREDIT_ANALYST,
  AD_GROUPS.ABTOOLS_PROCESSING_STAFF,
  AD_GROUPS.AD_ABTOOLS_PROD,
];

const DEVL_FULL_ACCESS_GROUPS = [
  AD_GROUPS.ABTOOLS_NON_PROD,
];

// qual keeps its existing full-access groups and additionally allows ABTOOLS_NON_PROD.
const QUAL_FULL_ACCESS_GROUPS = [
  AD_GROUPS.ABTOOLS_SYSTEM_ADMIN,
  AD_GROUPS.ABTOOLS_CREDIT_ANALYST,
  AD_GROUPS.ABTOOLS_PROCESSING_STAFF,
  AD_GROUPS.ABTOOLS_NON_PROD,
];

// ABTOOLS_PROD_SUPPORT has read-only access (with SSN masking) in devl, qual and prod.
const READONLY_GROUPS_BY_ENV = [AD_GROUPS.ABTOOLS_PROD_SUPPORT];

/** Returns true when the current build targets devl/cert. */
const isDevlEnv = (): boolean =>
  process.env.APP_ENV === 'devl';

/** Returns true when the current build targets qual. */
const isQualEnv = (): boolean =>
  process.env.APP_ENV === 'qual';

/** Full-access groups for the current environment. */
export const getFullAccessGroups = (): string[] => {
  if (isDevlEnv()) return DEVL_FULL_ACCESS_GROUPS;
  if (isQualEnv()) return QUAL_FULL_ACCESS_GROUPS;
  return PROD_FULL_ACCESS_GROUPS; // prod: ABTOOLS_NON_PROD is NOT allowed
};

/**
 * Readonly groups for the current environment.
 * ABTOOLS_PROD_SUPPORT has readonly access on devl, qual and prod.
 */
export const getReadOnlyGroups = (): string[] => READONLY_GROUPS_BY_ENV;

/**
 * Returns true if the user holds at least one authorised AD group for the
 * current environment.
 * @param adGroups - already upper-cased list from Okta
 */
export const isAuthorizedUser = (adGroups: string[]): boolean => {
  const allowed = [...getFullAccessGroups(), ...getReadOnlyGroups()];
  return adGroups.some((g) => allowed.includes(g));
};

/**
 * Returns true for users who only have readonly access (ABTOOLS_PROD_SUPPORT on devl/prod).
 * @param adGroups - already upper-cased list from Okta
 */
export const isReadOnlyUser = (adGroups: string[]): boolean => {
  const readOnlyGroups = getReadOnlyGroups();
  const fullAccessGroups = getFullAccessGroups();
  return (
    readOnlyGroups.length > 0 &&
    adGroups.some((g) => readOnlyGroups.includes(g)) &&
    !adGroups.some((g) => fullAccessGroups.includes(g))
  );
};

/**
 * Involved-party role descriptions whose SSN/Tax ID is always shown in full,
 * even for readonly (ABTOOLS_PROD_SUPPORT) users.
 */
export const SSN_UNMASKED_ROLE_DESCS = ['Data Coordinator', 'Associate Credit Analyst'];

// Keep legacy exports for qual/prod groups (used nowhere new, but avoids breakage)
export const FULL_ACCESS_GROUPS = PROD_FULL_ACCESS_GROUPS;
export const READONLY_GROUPS = READONLY_GROUPS_BY_ENV;
export const ALL_AUTHORIZED_GROUPS = [...PROD_FULL_ACCESS_GROUPS, AD_GROUPS.ABTOOLS_NON_PROD, ...READONLY_GROUPS_BY_ENV];

/** Returns a partially-masked SSN/Tax ID string showing only the last 4 digits. */
export const maskSsnTaxId = (ssn: string | null | undefined): string => {
  if (!ssn) return '';
  const digits = ssn.replace(/\D/g, '');
  const last4 = digits.slice(-4);
  return `***-**-${last4}`;
};
